/**
 * LLM Profile Configurator
 * 
 * Admin UI for managing LLM model configurations across all calls.
 * Primary tool for cost optimization and model tuning.
 * 
 * Features:
 * - View all 93+ LLM calls with current configuration
 * - Swap models via vendor-grouped dropdown
 * - Adjust parameters (temperature, max_tokens, top_p)
 * - Dual cost mode toggle (Modeled vs Actual)
 * - Info tooltips with call descriptions
 * - User profile assignment for A/B testing
 * 
 * Mission 33 Sprint 5, Phase 5 (Feb 5, 2026)
 */

import React, { useState, useEffect } from 'react';
import { Info, ChevronDown, Save, RefreshCw, DollarSign, BarChart3, Lightbulb } from 'lucide-react';
import { API_BASE_URL } from '../../utils/apiConfig';
import { storage } from '../../services/storage';

interface LLMCall {
  call_name: string;
  display_name: string;
  category: string;
  description_short: string;
  current_model: string;
  vendor: string;
  temperature: number;
  max_tokens: number;
  top_p: number;
  reasoning_effort: string | null;
  supports_reasoning: boolean;
  adaptive_thinking_effort: string | null;
  supports_adaptive_thinking: boolean;
  // null = the registry has no estimate. Unknown is not zero: these render
  // "—", never 0 / $0.0000 (WS6 defect 2 — missing-as-free lied for months).
  estimated_input_tokens: number | null;
  estimated_output_tokens: number | null;
  /**
   * The server's modeled $/call, priced against the REGISTRY model
   * (llm_call_definitions.current_model) — null when the call has no token
   * estimates. Display never reads this directly: it goes through
   * modeledCostPerCall(), which reprices against the model actually shown on
   * the row, because the profile overlay may have swapped it (WS6 defect 3).
   */
  cost_per_call_modeled: number | null;
  // null = frequency unknown for that tier (distinct from a legitimate 0,
  // e.g. Essentials has no ad-hoc chat).
  frequency_trial: number | null;
  frequency_full: number | null;
  frequency_extended: number | null;
  priority: number;
  // Enrichment context (Migration 1101)
  system_role: string | null;
  downstream_impact: string | null;
  pipeline_context: string | null;
  latency_tier: string | null;
  latency_budget_ms: number | null;
  volume_pattern: string | null;
  estimated_weekly_volume_per_user: number | null;
  estimated_input_tokens_with_context: number | null;
  estimated_reasoning_tokens: number | null;
  reasoning_justification: string | null;
  output_criticality: string | null;
  accuracy_requirement: string | null;
  deprecated: boolean;
  deprecated_reason: string | null;
  /**
   * Set by the profile overlay: the registry model the server priced
   * `cost_per_call_modeled` against, kept so the cost display can tell "the
   * model the server priced" from "the model on display".
   */
  registry_model?: string;
}

interface Model {
  model_name: string;
  model_display: string;
  model_family: string;
  input_cost: number;
  output_cost: number;
  max_input_tokens: number;
  max_output_tokens: number;
  active: boolean;
  supports_reasoning: boolean;
  supports_json_mode: boolean;
  supports_adaptive_thinking: boolean;
  model_type: string;
}

interface Vendor {
  vendor_name: string;
  vendor_display: string;
  vendor_active: boolean;
  models: Model[];
}

type CostMode = 'modeled' | 'actual';
type TierFilter = 'trial' | 'full' | 'extended';

interface LLMProfileConfiguratorProps {
  profileName?: string;
  onProfileUpdated?: () => void;
}

export function LLMProfileConfigurator({ 
  profileName = 'default',
  onProfileUpdated
}: LLMProfileConfiguratorProps) {
  const [calls, setCalls] = useState<LLMCall[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [costMode, setCostMode] = useState<CostMode>('modeled');
  const [tierFilter, setTierFilter] = useState<TierFilter>('full');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [showDeprecated, setShowDeprecated] = useState(false);
  const [expandedCall, setExpandedCall] = useState<string | null>(null);
  const [editingCall, setEditingCall] = useState<string | null>(null);
  const [savingCall, setSavingCall] = useState<string | null>(null);
  
  // Editing state for current call
  const [editModel, setEditModel] = useState<string>('');
  const [editTemp, setEditTemp] = useState<number>(0.7);
  const [editMaxTokens, setEditMaxTokens] = useState<number>(1000);
  const [editTopP, setEditTopP] = useState<number>(1.0);
  const [editReasoningEffort, setEditReasoningEffort] = useState<string | null>(null);
  const [editAdaptiveThinking, setEditAdaptiveThinking] = useState<string | null>(null);
  // Frequencies are edited as STRINGS so "unknown" survives the round trip.
  // '' = the registry has no frequency for that tier; it is not 0. Seeding 0
  // into the form and posting it back would write a fabricated 0 into
  // llm_call_definitions on every unrelated model edit, and downstream
  // ("0/wk × any price = $0/mo") a fabricated 0 is indistinguishable from a
  // real one. An admin who types 0 still saves a real 0.
  const [editFreqTrial, setEditFreqTrial] = useState<string>('');
  const [editFreqFull, setEditFreqFull] = useState<string>('');
  const [editFreqExtended, setEditFreqExtended] = useState<string>('');
  
  useEffect(() => {
    loadData();
  }, [categoryFilter, profileName, showDeprecated]);
  
  async function loadData() {
    setLoading(true);
    
    try {
      // Get admin token for authentication
      const token = await storage.getItem('admin_token');
      if (!token) {
        throw new Error('Admin token not found. Please log in again.');
      }
      
      // Load calls
      const params = new URLSearchParams();
      if (categoryFilter !== 'all') params.set('category', categoryFilter);
      if (showDeprecated) params.set('include_deprecated', 'true');
      const qs = params.toString();
      const callsRes = await fetch(`${API_BASE_URL}/api/admin/llm-profiles/calls${qs ? `?${qs}` : ''}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      
      if (!callsRes.ok) {
        throw new Error(`Failed to fetch calls: ${callsRes.status} ${callsRes.statusText}`);
      }
      
      const callsData = await callsRes.json();
      let callRows = callsData.calls || [];

      // Load models (vendor-grouped)
      const modelsRes = await fetch(`${API_BASE_URL}/api/admin/llm-profiles/models`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!modelsRes.ok) {
        throw new Error(`Failed to fetch models: ${modelsRes.status} ${modelsRes.statusText}`);
      }

      const modelsData = await modelsRes.json();
      setVendors(modelsData.vendors || []);

      // Overlay the SELECTED profile's configs. /calls returns the global
      // registry (llm_call_definitions.current_model) joined only to the
      // DEFAULT profile, so a custom profile's models never display without
      // this overlay — the edit form would also initialize from the wrong
      // (global) values when editing a non-default profile.
      // UNCONDITIONAL by design (SOW WS1.4): the profile literally named
      // 'default' must ALSO render from its own profile_call_configs. The
      // registry has drifted from it, so overlaying only non-'default'
      // profiles let a routine save launder registry values into the live
      // config — and once promotion moves is_default to another profile,
      // the name 'default' stops meaning "the live profile" at all.
      if (profileName) {
        const profileRes = await fetch(
          `${API_BASE_URL}/api/admin/llm-profiles/profiles/${encodeURIComponent(profileName)}/calls`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            credentials: 'include'
          }
        );
        if (profileRes.ok) {
          const profileData = await profileRes.json();
          const configByCall = new Map<string, any>(
            (profileData.configurations || []).map((c: any) => [c.call_name, c] as [string, any])
          );
          const modelIndex = new Map<string, any>();
          (modelsData.vendors || []).forEach((v: any) =>
            (v.models || []).forEach((m: any) =>
              modelIndex.set(m.model_name, { ...m, vendor_name: v.vendor_name })
            )
          );
          callRows = callRows.map((row: any) => {
            const cfg = configByCall.get(row.call_name);
            if (!cfg) return row;
            const md = modelIndex.get(cfg.model_name);
            return {
              ...row,
              // The registry model the server priced cost_per_call_modeled
              // against — kept so modeledCostPerCall() can tell when the
              // server's figure no longer describes the model on display
              // (WS6 defect 3: the PR #2864 overlay never swapped the cost,
              // so a candidate on terra displayed the live profile's price).
              registry_model: row.current_model,
              current_model: cfg.model_name,
              temperature: cfg.temperature ?? row.temperature,
              max_tokens: cfg.max_tokens ?? row.max_tokens,
              top_p: cfg.top_p ?? row.top_p,
              reasoning_effort: cfg.reasoning_effort ?? null,
              adaptive_thinking_effort: cfg.adaptive_thinking_effort ?? null,
              // NOTE: /models serves prices as `input_cost`/`output_cost` —
              // the previous spread here read `md.input_cost_usd_per_mtok`,
              // a field that endpoint never sends, so it was a dead write.
              // Cost is now repriced at render time (modeledCostPerCall);
              // only capability flags and the vendor label are overlaid.
              ...(md ? {
                supports_reasoning: md.supports_reasoning ?? row.supports_reasoning,
                supports_adaptive_thinking: md.supports_adaptive_thinking ?? row.supports_adaptive_thinking,
                vendor: md.vendor_name ?? row.vendor
              } : {})
            };
          });
        } else {
          console.error(`Failed to overlay profile configs: ${profileRes.status} ${profileRes.statusText}`);
        }
      }
      setCalls(callRows);
      
    } catch (error) {
      console.error('Failed to load LLM profile data:', error);
    } finally {
      setLoading(false);
    }
  }
  
  function startEditing(call: LLMCall) {
    setEditingCall(call.call_name);
    setEditModel(call.current_model);
    setEditTemp(call.temperature);
    setEditMaxTokens(call.max_tokens);
    setEditTopP(call.top_p || 1.0);
    setEditReasoningEffort(call.reasoning_effort);
    setEditAdaptiveThinking(call.adaptive_thinking_effort || null);
    // `?? ''` not `|| 0`: a real 0 must stay 0, and a null must stay blank.
    setEditFreqTrial(call.frequency_trial == null ? '' : String(call.frequency_trial));
    setEditFreqFull(call.frequency_full == null ? '' : String(call.frequency_full));
    setEditFreqExtended(call.frequency_extended == null ? '' : String(call.frequency_extended));
  }
  
  function cancelEditing() {
    setEditingCall(null);
    setEditModel('');
    setEditTemp(0.7);
    setEditMaxTokens(1000);
    setEditTopP(1.0);
    setEditReasoningEffort(null);
    setEditAdaptiveThinking(null);
    setEditFreqTrial('');
    setEditFreqFull('');
    setEditFreqExtended('');
  }
  
  /**
   * Edit-form frequency -> request value.
   *
   * null tells the server "leave this tier alone": the PUT handler
   * (routers/admin_llm_profiles.py, UpdateCallConfigRequest.frequency_* are
   * Optional[int] = None) builds its UPDATE only from the frequencies that
   * arrive non-None, so a null never touches llm_call_definitions. That is how
   * an admin who came here to change a MODEL leaves an unseeded call's
   * frequencies unseeded instead of stamping 0s across the registry.
   *
   * A typed 0 is a real answer ("this call never happens on this tier") and is
   * sent as 0. Anything unparseable is treated as unknown rather than guessed.
   */
  function freqForRequest(value: string): number | null {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    const parsed = parseInt(trimmed, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }

  async function saveConfiguration(call: LLMCall) {
    setSavingCall(call.call_name);
    
    try {
      // Get admin token for authentication
      const token = await storage.getItem('admin_token');
      if (!token) {
        throw new Error('Admin token not found. Please log in again.');
      }
      
      const response = await fetch(
        `${API_BASE_URL}/api/admin/llm-profiles/profiles/${profileName}/calls/${call.call_name}`,
        {
          method: 'PUT',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json' 
          },
          credentials: 'include',
          body: JSON.stringify({
            model_name: editModel,
            temperature: doesEditModelSupportReasoning() ? 1.0 : editTemp,
            max_tokens: editMaxTokens,
            top_p: doesEditModelSupportReasoning() ? 1.0 : editTopP,
            reasoning_effort: doesEditModelSupportReasoning() ? editReasoningEffort : null,
            adaptive_thinking_effort: doesEditModelSupportAdaptiveThinking() ? editAdaptiveThinking : null,
            // null = "unknown, do not write" (see freqForRequest).
            frequency_trial: freqForRequest(editFreqTrial),
            frequency_full: freqForRequest(editFreqFull),
            frequency_extended: freqForRequest(editFreqExtended)
          })
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        // Success - reload data
        await loadData();
        cancelEditing();

        // Show warnings if any (e.g. auto-coerced temperature on reasoning model)
        const warnings = result.warnings || [];
        if (warnings.length > 0) {
          alert(`Saved with notes:\n${warnings.join('\n')}`);
        } else {
          alert('Configuration saved successfully!');
        }

        // Notify parent component that profile was updated
        if (onProfileUpdated) {
          onProfileUpdated();
        }
      } else {
        const errorText = await response.text();
        alert(`Failed to save: ${errorText}`);
        console.error('Save error:', errorText);
      }
    } catch (error) {
      console.error('Save failed:', error);
      alert('Save failed - check console');
    } finally {
      setSavingCall(null);
    }
  }
  
  /**
   * Models billed per audio-minute rather than per token.
   *
   * Mirror of the server's rule (`_PER_MINUTE_BILLED_NAME_TOKENS` /
   * `_PER_MINUTE_BILLED_MODEL_TYPES` in services/llm_profile_promotion.py):
   * per-minute when the model_type says so, or — for a model missing from the
   * /models list — when the name carries one of the family tokens. These rows
   * store a per-minute rate in the per-MTok columns, so token arithmetic on
   * them yields a number with no meaning; the column shows "—" instead.
   * Keep the two lists in step: a divergence would silently re-admit a
   * fabricated price on one side.
   */
  function isPerMinuteBilled(modelName: string): boolean {
    const lowered = (modelName || '').toLowerCase();
    const PER_MINUTE_NAME_TOKENS = ['transcribe', 'whisper', 'tts', 'audio', 'realtime'];
    const PER_MINUTE_MODEL_TYPES = ['audio', 'transcribe', 'speech'];
    if (PER_MINUTE_NAME_TOKENS.some(token => lowered.includes(token))) return true;
    const modelType = getModelInfo(modelName)?.model_type;
    return modelType != null && PER_MINUTE_MODEL_TYPES.includes(modelType.toLowerCase());
  }

  /**
   * Modeled $/call for the model ACTUALLY shown on the row.
   *
   * The server's `cost_per_call_modeled` is priced against the registry model,
   * so once the profile overlay swaps `current_model` that figure describes
   * the wrong model (WS6 defect 3). Reprice here from the /models list, whose
   * per-MTok prices come straight from model_definitions — the same table the
   * usage ledger snapshots at write time.
   *
   * null = honestly unpriceable: no token estimate, or no price for the
   * displayed model. Rendered as "—", never $0.0000 (WS6 defect 2) — a
   * missing estimate rendered as free is exactly the lie this sweep removes.
   * Registry estimates also carry no reasoning tokens, which is one more
   * reason the column says "modeled": measured (ledger) figures live in the
   * usage-analytics surfaces.
   */
  function modeledCostPerCall(call: LLMCall): number | null {
    // Per-audio-minute models first: their per-MTok columns carry a per-minute
    // rate as a storage convenience, so tokens × price is arithmetic on two
    // different units. Whatever it produces is not a price. The server has
    // begun sending null cost_per_call_modeled for these too; both layers land
    // on "—".
    if (isPerMinuteBilled(call.current_model)) return null;
    if (call.estimated_input_tokens == null || call.estimated_output_tokens == null) {
      return null;
    }
    const model = getModelInfo(call.current_model);
    if (model) {
      return (
        (call.estimated_input_tokens / 1_000_000) * model.input_cost +
        (call.estimated_output_tokens / 1_000_000) * model.output_cost
      );
    }
    // The displayed model is missing from /models (e.g. deprecated models are
    // filtered out). The server's figure was priced against the registry
    // model, so it is reusable only when that is the model on display.
    if (!call.registry_model || call.registry_model === call.current_model) {
      return typeof call.cost_per_call_modeled === 'number' &&
        Number.isFinite(call.cost_per_call_modeled)
        ? call.cost_per_call_modeled
        : null;
    }
    return null;
  }

  /**
   * null = not priceable (missing token estimate, model price, or tier
   * frequency). Unknown is not zero: nulls are excluded from the summary
   * total — which is then labeled a floor — and the row renders "—".
   */
  function calculateCostPerWeek(call: LLMCall): number | null {
    const perCall = modeledCostPerCall(call);
    if (perCall === null) return null;
    const frequency = tierFilter === 'trial' ? call.frequency_trial :
                     tierFilter === 'full' ? call.frequency_full :
                     call.frequency_extended;
    if (frequency == null) return null;
    return perCall * frequency;
  }

  function calculateCostPerMonth(call: LLMCall): number | null {
    const weekly = calculateCostPerWeek(call);
    return weekly === null ? null : weekly * 4.33;  // Average weeks per month
  }

  // Helper: Look up a model in the vendor-grouped /models list. Used for edit
  // form capability checks AND for repricing the cost columns.
  function getModelInfo(modelName: string): Model | null {
    for (const vendor of vendors) {
      for (const model of vendor.models) {
        if (model.model_name === modelName) {
          return model;
        }
      }
    }
    return null;
  }

  // Helper: Get the currently selected model object
  function getEditModelInfo(): Model | null {
    return getModelInfo(editModel);
  }

  // Helper: Check if the currently selected edit model supports reasoning
  function doesEditModelSupportReasoning(): boolean {
    return getEditModelInfo()?.supports_reasoning ?? false;
  }

  // Helper: Check if the currently selected edit model supports adaptive thinking (Claude 4.6+)
  function doesEditModelSupportAdaptiveThinking(): boolean {
    return getEditModelInfo()?.supports_adaptive_thinking ?? false;
  }

  // Helper: Get max output tokens for the selected model
  function getEditModelMaxOutputTokens(): number {
    return getEditModelInfo()?.max_output_tokens ?? 16000;
  }

  // Helper: Get vendor for a model name
  function getModelVendor(modelName: string): string | null {
    for (const vendor of vendors) {
      for (const model of vendor.models) {
        if (model.model_name === modelName) {
          return vendor.vendor_name;
        }
      }
    }
    return null;
  }
  
  const categories = ['all', ...new Set(calls.map(c => c.category))];

  // Only priceable calls enter the total; the rest are COUNTED and the total
  // is labeled a floor. Summing nulls as $0 would make missing estimates read
  // as free — the silent-$0 class this sweep removes.
  const weeklyCosts = calls.map(call => calculateCostPerWeek(call));
  const pricedWeekly = weeklyCosts.filter((cost): cost is number => cost !== null);
  const unpricedCallCount = weeklyCosts.length - pricedWeekly.length;
  const totalCostPerWeek = pricedWeekly.reduce((sum, cost) => sum + cost, 0);
  const totalCostPerMonth = totalCostPerWeek * 4.33;
  
  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--cedar-primary)' }} />
        <p style={{ marginTop: '16px', color: 'var(--text-secondary)' }}>Loading LLM configurations...</p>
      </div>
    );
  }
  
  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: '700', color: 'var(--cedar-primary)', marginBottom: '8px' }}>
          LLM Model Configurator
        </h1>
        <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
          Configure models, tune parameters, and analyze costs across all {calls.length} LLM calls
        </p>
      </div>
      
      {/* Cost Summary Card */}
      <div style={{
        background: 'var(--surface-green-soft)',
        border: '2px solid var(--cedar-primary)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
            Modeled Cost ({tierFilter.toUpperCase()} Tier) — registry estimates × current model prices
          </div>
          <div style={{ fontSize: '32px', fontWeight: '700', color: 'var(--cedar-primary)' }}>
            ${totalCostPerMonth.toFixed(2)}<span style={{ fontSize: '16px', fontWeight: '400' }}>/month</span>
          </div>
          <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
            ${totalCostPerWeek.toFixed(2)}/week · {pricedWeekly.length} of {calls.length} calls priced
          </div>
          {unpricedCallCount > 0 && (
            <div style={{ fontSize: '12px', color: 'var(--status-warning-text)', marginTop: '6px', maxWidth: '620px' }}>
              {unpricedCallCount} call(s) have no token estimate or {tierFilter}-tier frequency in the
              registry and are excluded from this total — it is a floor, not the whole spend. Not
              counted as $0.
            </div>
          )}
          {costMode === 'actual' && (
            <div style={{ fontSize: '12px', color: 'var(--status-warning-text)', marginTop: '6px', maxWidth: '620px' }}>
              Measured (ledger) costs are not wired into this table — the figures shown remain
              modeled. For measured spend from llm_usage_actual, use LLM Usage Analytics.
            </div>
          )}
        </div>
        
        <div style={{ display: 'flex', gap: '12px' }}>
          <DollarSign size={48} color="var(--cedar-primary)" />
        </div>
      </div>
      
      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: '16px',
        marginBottom: '24px',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        {/* Cost Mode Toggle */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-strong)' }}>Cost Mode:</span>
          <button
            onClick={() => setCostMode('modeled')}
            style={{
              padding: '8px 16px',
              background: costMode === 'modeled' ? 'var(--cedar-primary)' : 'white',
              color: costMode === 'modeled' ? 'white' : 'var(--cedar-primary)',
              border: '2px solid var(--cedar-primary)',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Modeled (V4)
          </button>
          <button
            onClick={() => setCostMode('actual')}
            style={{
              padding: '8px 16px',
              background: costMode === 'actual' ? 'var(--cedar-primary)' : 'white',
              color: costMode === 'actual' ? 'white' : 'var(--cedar-primary)',
              border: '2px solid var(--cedar-primary)',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Actual (Sprint 6+)
          </button>
        </div>
        
        {/* Tier Filter */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-strong)' }}>Tier:</span>
          {(['trial', 'full', 'extended'] as TierFilter[]).map(tier => (
            <button
              key={tier}
              onClick={() => setTierFilter(tier)}
              style={{
                padding: '8px 16px',
                background: tierFilter === tier ? 'var(--cedar-primary)' : 'white',
                color: tierFilter === tier ? 'white' : 'var(--cedar-primary)',
                border: '2px solid var(--cedar-primary)',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                textTransform: 'capitalize'
              }}
            >
              {tier}
            </button>
          ))}
        </div>
        
        {/* Category Filter */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-strong)' }}>Category:</span>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '2px solid var(--cedar-primary)',
              borderRadius: '6px',
              fontSize: '14px',
              background: 'white',
              color: 'var(--cedar-primary)',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat === 'all' ? 'All Categories' : cat}</option>
            ))}
          </select>
        </div>
        
        {/* Show Deprecated Toggle */}
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--text-secondary)', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={showDeprecated}
            onChange={(e) => setShowDeprecated(e.target.checked)}
            style={{ accentColor: 'var(--cedar-primary)' }}
          />
          Show deprecated
        </label>

        {/* Refresh Button */}
        <button
          onClick={loadData}
          style={{
            padding: '8px 16px',
            background: 'white',
            color: 'var(--cedar-primary)',
            border: '2px solid var(--cedar-primary)',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginLeft: 'auto'
          }}
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>
      
      {/* Calls Table */}
      <div style={{
        background: 'white',
        border: '1px solid var(--border)',
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        {/* Table Header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1.5fr 0.7fr 0.8fr 0.9fr 0.8fr 0.8fr 120px',
          gap: '12px',
          padding: '16px',
          background: 'var(--surface-subtle)',
          borderBottom: '1px solid var(--border)',
          fontSize: '13px',
          fontWeight: '600',
          color: 'var(--text-secondary)'
        }}>
          <div>CALL NAME</div>
          <div>CURRENT MODEL</div>
          <div>TEMP</div>
          <div>MAX TOKENS</div>
          <div>REASONING</div>
          {/* "Modeled" in the header so the provenance is explicit — these are
              registry estimates × current prices, not ledger dollars. */}
          <div>MODELED $/CALL</div>
          <div>MODELED $/MO</div>
          <div style={{ textAlign: 'center' }}>ACTIONS</div>
        </div>
        
        {/* Table Body */}
        {calls.map(call => {
          const isEditing = editingCall === call.call_name;
          const isSaving = savingCall === call.call_name;
          const costPerCall = modeledCostPerCall(call);
          const costPerMonth = calculateCostPerMonth(call);
          // Why a cell is "—" instead of a dollar figure. Distinct causes get
          // distinct tooltips so a blank is a diagnosis, not a shrug.
          const noEstimate =
            call.estimated_input_tokens == null || call.estimated_output_tokens == null;
          const perCallGapTitle = isPerMinuteBilled(call.current_model)
            ? 'billed per audio-minute — token arithmetic not meaningful'
            : noEstimate
              ? 'no token estimate — see measured analytics'
              : 'no price for this model — see measured analytics';
          const monthGapTitle = costPerCall === null
            ? perCallGapTitle
            : `no ${tierFilter}-tier frequency in the registry — see measured analytics`;

          return (
            <div key={call.call_name}>
              {/* Main Row */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1.5fr 0.7fr 0.8fr 0.9fr 0.8fr 0.8fr 120px',
                gap: '12px',
                padding: '16px',
                borderBottom: '1px solid var(--cedar-limestone)',
                alignItems: 'center',
                background: isEditing ? 'var(--note-bg)' : 'white'
              }}>
                {/* Call Name */}
                <div>
                  <div style={{ 
                    fontSize: '14px', 
                    fontWeight: '600', 
                    color: 'var(--text-primary)',
                    marginBottom: '4px'
                  }}>
                    {call.display_name}
                    {call.deprecated && (
                      <span style={{ marginLeft: '8px', padding: '1px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: '700', background: 'var(--status-error-bg)', color: 'var(--status-error-text)', textTransform: 'uppercase' }}>Deprecated</span>
                    )}
                  </div>
                  <div style={{ fontSize: '12px', color: call.deprecated ? 'var(--border-strong)' : 'var(--text-tertiary)' }}>
                    {call.call_name}
                  </div>
                  <div style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    background: 'var(--surface-green-soft)',
                    color: 'var(--cedar-primary)',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: '500',
                    marginTop: '4px'
                  }}>
                    {call.category}
                  </div>
                </div>
                
                {/* Current Model */}
                <div>
                  {isEditing ? (
                    <>
                    <select
                      value={editModel}
                      onChange={(e) => {
                        const newModel = e.target.value;
                        const oldVendor = getModelVendor(editModel);
                        const newVendor = getModelVendor(newModel);
                        setEditModel(newModel);
                        // Reset reasoning/adaptive when switching capability profiles
                        const newInfo = (() => { for (const v of vendors) for (const m of v.models) if (m.model_name === newModel) return m; return null; })();
                        if (newInfo && !newInfo.supports_reasoning) setEditReasoningEffort(null);
                        if (newInfo && !newInfo.supports_adaptive_thinking) setEditAdaptiveThinking(null);
                      }}
                      style={{
                        width: '100%',
                        padding: '6px 8px',
                        border: '2px solid var(--cedar-primary)',
                        borderRadius: '4px',
                        fontSize: '13px'
                      }}
                    >
                      {vendors.map(vendor => (
                        <optgroup key={vendor.vendor_name} label={vendor.vendor_display}>
                          {vendor.models.map(model => (
                            <option key={model.model_name} value={model.model_name}>
                              {model.model_display}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    {/* Vendor change notice */}
                    {getModelVendor(editModel) !== getModelVendor(call.current_model) && getModelVendor(editModel) && (
                      <div style={{ fontSize: '11px', color: 'var(--status-orange)', marginTop: '4px', fontWeight: 500 }}>
                        Vendor change: some features differ across vendors
                      </div>
                    )}
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-primary)' }}>
                        {call.current_model}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '2px' }}>
                        {call.vendor}
                      </div>
                    </>
                  )}
                </div>
                
                {/* Temperature */}
                <div>
                  {isEditing ? (
                    doesEditModelSupportReasoning() ? (
                      <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }} title="Reasoning models use fixed temperature 1.0">
                        1.0 (fixed)
                      </span>
                    ) : (
                    <input
                      type="number"
                      value={editTemp}
                      onChange={(e) => setEditTemp(parseFloat(e.target.value))}
                      min="0"
                      max="2"
                      step="0.1"
                      style={{
                        width: '80px',
                        padding: '6px 8px',
                        border: '2px solid var(--cedar-primary)',
                        borderRadius: '4px',
                        fontSize: '13px'
                      }}
                    />
                    )
                  ) : (
                    <span style={{ fontSize: '13px', color: 'var(--text-strong)' }}>
                      {call.temperature.toFixed(1)}
                    </span>
                  )}
                </div>
                
                {/* Max Tokens */}
                <div>
                  {isEditing ? (
                    <input
                      type="number"
                      value={editMaxTokens}
                      onChange={(e) => setEditMaxTokens(parseInt(e.target.value))}
                      min="100"
                      max={getEditModelMaxOutputTokens()}
                      step="100"
                      style={{
                        width: '100px',
                        padding: '6px 8px',
                        border: '2px solid var(--cedar-primary)',
                        borderRadius: '4px',
                        fontSize: '13px'
                      }}
                    />
                  ) : (
                    <span style={{ fontSize: '13px', color: 'var(--text-strong)' }}>
                      {call.max_tokens.toLocaleString()}
                    </span>
                  )}
                </div>
                
                {/* Reasoning Effort */}
                <div>
                  {isEditing ? (
                    doesEditModelSupportReasoning() ? (
                      <select
                        value={editReasoningEffort || 'medium'}
                        onChange={(e) => setEditReasoningEffort(e.target.value)}
                        style={{
                          width: '100px',
                          padding: '6px 8px',
                          border: '2px solid var(--cedar-primary)',
                          borderRadius: '4px',
                          fontSize: '13px'
                        }}
                      >
                        <option value="none">None</option>
                        <option value="minimal">Minimal</option>
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="xhigh">XHigh</option>
                        <option value="max">Max</option>
                      </select>
                    ) : (
                      <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>N/A</span>
                    )
                  ) : (
                    call.supports_reasoning && call.reasoning_effort ? (
                      <span style={{ 
                        fontSize: '12px', 
                        color: 'var(--cedar-primary)',
                        background: 'var(--cedar-subtle)',
                        padding: '2px 8px',
                        borderRadius: '10px',
                        fontWeight: 500
                      }}>
                        {call.reasoning_effort}
                      </span>
                    ) : (
                      <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>—</span>
                    )
                  )}
                </div>
                
                {/* Modeled Frequencies + Top P (expanded editing panel) */}
                {isEditing && (
                  <div style={{
                    gridColumn: '1 / -1',
                    marginTop: '12px',
                    padding: '12px',
                    background: 'var(--surface-subtle)',
                    borderRadius: '6px',
                    border: '1px solid var(--border)'
                  }}>
                    <div style={{ 
                      fontSize: '13px', 
                      fontWeight: 600, 
                      marginBottom: '10px',
                      color: 'var(--cedar-primary)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <BarChart3 size={14} />Advanced Parameters & Modeled Frequencies
                    </div>
                    {/* Top P */}
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{
                        fontSize: '12px',
                        color: 'var(--text-secondary)',
                        display: 'block',
                        marginBottom: '4px'
                      }}>
                        Top P:
                      </label>
                      {doesEditModelSupportReasoning() ? (
                        <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }} title="Reasoning models don't support top_p">
                          N/A (reasoning model)
                        </span>
                      ) : (
                        <>
                          <input
                            type="number"
                            value={editTopP}
                            onChange={(e) => setEditTopP(parseFloat(e.target.value))}
                            min="0"
                            max="1"
                            step="0.05"
                            style={{
                              width: '100px',
                              padding: '6px 8px',
                              border: '1px solid var(--border-strong)',
                              borderRadius: '4px',
                              fontSize: '13px'
                            }}
                          />
                          <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginLeft: '8px' }}>
                            (0.0 - 1.0, default 1.0)
                          </span>
                        </>
                      )}
                    </div>
                    {/* Adaptive Thinking (Claude 4.6+ only) */}
                    {doesEditModelSupportAdaptiveThinking() && (
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{
                          fontSize: '12px',
                          color: 'var(--text-secondary)',
                          display: 'block',
                          marginBottom: '4px'
                        }}>
                          Adaptive Thinking Effort:
                        </label>
                        <select
                          value={editAdaptiveThinking || ''}
                          onChange={(e) => setEditAdaptiveThinking(e.target.value || null)}
                          style={{
                            width: '120px',
                            padding: '6px 8px',
                            border: '1px solid var(--border-strong)',
                            borderRadius: '4px',
                            fontSize: '13px'
                          }}
                        >
                          <option value="">Default</option>
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                          <option value="max">Max</option>
                        </select>
                        <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginLeft: '8px' }}>
                          Claude extended thinking budget
                        </span>
                      </div>
                    )}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: '12px'
                    }}>
                      {/* Trial */}
                      <div>
                        <label style={{ 
                          fontSize: '12px', 
                          color: 'var(--text-secondary)',
                          display: 'block',
                          marginBottom: '4px'
                        }}>
                          Trial:
                        </label>
                        <input
                          type="number"
                          value={editFreqTrial}
                          onChange={(e) => setEditFreqTrial(e.target.value)}
                          placeholder="unknown"
                          min="0"
                          max="1000"
                          step="1"
                          style={{
                            width: '100%',
                            padding: '6px 8px',
                            border: '2px solid var(--cedar-primary)',
                            borderRadius: '4px',
                            fontSize: '13px'
                          }}
                        />
                      </div>
                      
                      {/* Full */}
                      <div>
                        <label style={{ 
                          fontSize: '12px', 
                          color: 'var(--text-secondary)',
                          display: 'block',
                          marginBottom: '4px'
                        }}>
                          Full:
                        </label>
                        <input
                          type="number"
                          value={editFreqFull}
                          onChange={(e) => setEditFreqFull(e.target.value)}
                          placeholder="unknown"
                          min="0"
                          max="1000"
                          step="1"
                          style={{
                            width: '100%',
                            padding: '6px 8px',
                            border: '2px solid var(--cedar-primary)',
                            borderRadius: '4px',
                            fontSize: '13px'
                          }}
                        />
                      </div>
                      
                      {/* Extended */}
                      <div>
                        <label style={{ 
                          fontSize: '12px', 
                          color: 'var(--text-secondary)',
                          display: 'block',
                          marginBottom: '4px'
                        }}>
                          Extended:
                        </label>
                        <input
                          type="number"
                          value={editFreqExtended}
                          onChange={(e) => setEditFreqExtended(e.target.value)}
                          placeholder="unknown"
                          min="0"
                          max="1000"
                          step="1"
                          style={{
                            width: '100%',
                            padding: '6px 8px',
                            border: '2px solid var(--cedar-primary)',
                            borderRadius: '4px',
                            fontSize: '13px'
                          }}
                        />
                      </div>
                    </div>
                    <div style={{
                      fontSize: '11px',
                      color: 'var(--text-secondary)',
                      marginTop: '8px',
                      fontStyle: 'italic',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '6px'
                    }}>
                      <Lightbulb size={13} style={{ flexShrink: 0, marginTop: '1px' }} />These are modeled assumptions for cost analysis. Actual usage tracked separately. Leave a field blank to keep it unknown — blank is not saved as 0.
                    </div>
                  </div>
                )}
                
                {/* Modeled Cost Per Call — repriced against the model on
                    display; "—" when unpriceable, never $0.0000. */}
                <div style={{ fontSize: '13px', fontWeight: '500', color: 'var(--cedar-primary)' }}>
                  {costPerCall === null ? (
                    <span style={{ color: 'var(--text-tertiary)' }} title={perCallGapTitle}>—</span>
                  ) : (
                    `$${costPerCall.toFixed(4)}`
                  )}
                </div>

                {/* Modeled Cost Per Month */}
                <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>
                  {costPerMonth === null ? (
                    <span style={{ color: 'var(--text-tertiary)' }} title={monthGapTitle}>—</span>
                  ) : (
                    `$${costPerMonth.toFixed(2)}`
                  )}
                </div>
                
                {/* Actions */}
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                  {isEditing ? (
                    <>
                      <button
                        onClick={() => saveConfiguration(call)}
                        disabled={isSaving}
                        style={{
                          padding: '6px 12px',
                          background: 'var(--cedar-primary)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500',
                          cursor: isSaving ? 'wait' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <Save size={14} />
                        {isSaving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={cancelEditing}
                        disabled={isSaving}
                        style={{
                          padding: '6px 12px',
                          background: 'white',
                          color: 'var(--text-secondary)',
                          border: '1px solid var(--border)',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: isSaving ? 'wait' : 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => startEditing(call)}
                        style={{
                          padding: '6px 12px',
                          background: 'white',
                          color: 'var(--cedar-primary)',
                          border: '2px solid var(--cedar-primary)',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500',
                          cursor: 'pointer'
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setExpandedCall(expandedCall === call.call_name ? null : call.call_name)}
                        style={{
                          padding: '6px',
                          background: 'white',
                          color: 'var(--cedar-primary)',
                          border: '2px solid var(--cedar-primary)',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <Info size={14} />
                      </button>
                    </>
                  )}
                </div>
              </div>
              
              {/* Expanded Details — Enriched Context */}
              {expandedCall === call.call_name && (
                <div style={{
                  padding: '20px',
                  background: 'var(--surface-subtle)',
                  borderBottom: '1px solid var(--border)'
                }}>
                  {/* Deprecated Banner */}
                  {call.deprecated && (
                    <div style={{ padding: '10px 14px', background: 'var(--status-error-bg)', border: '1px solid var(--status-error-bg)', borderRadius: '8px', marginBottom: '16px', fontSize: '13px', color: 'var(--status-error-text)' }}>
                      <strong>Deprecated</strong> — {call.deprecated_reason || 'No active call site found.'}
                    </div>
                  )}

                  {/* System Role */}
                  {call.system_role && (
                    <div style={{ marginBottom: '14px' }}>
                      <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--cedar-primary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>System Role</div>
                      <div style={{ fontSize: '13px', color: 'var(--text-primary)', lineHeight: '1.6' }}>{call.system_role}</div>
                    </div>
                  )}

                  {/* Downstream Impact */}
                  {call.downstream_impact && (
                    <div style={{ marginBottom: '14px' }}>
                      <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--status-error-text)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>If Output is Wrong</div>
                      <div style={{ fontSize: '13px', color: 'var(--text-strong)', lineHeight: '1.6' }}>{call.downstream_impact}</div>
                    </div>
                  )}

                  {/* Pipeline Context */}
                  {call.pipeline_context && (
                    <div style={{ marginBottom: '14px' }}>
                      <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Pipeline Position</div>
                      <div style={{ fontSize: '13px', color: 'var(--text-strong)', lineHeight: '1.6' }}>{call.pipeline_context}</div>
                    </div>
                  )}

                  {/* Tags Row */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
                    {call.latency_tier && (
                      <span style={{
                        padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600',
                        background: call.latency_tier === 'critical' ? 'var(--status-error-bg)' : call.latency_tier === 'high' ? 'var(--status-warning-bg)' : call.latency_tier === 'medium' ? 'var(--status-cyan-bg)' : call.latency_tier === 'batch' ? 'var(--surface-muted)' : 'var(--status-success-bg)',
                        color: call.latency_tier === 'critical' ? 'var(--status-error-text)' : call.latency_tier === 'high' ? 'var(--status-warning-text)' : call.latency_tier === 'medium' ? 'var(--status-cyan-text)' : call.latency_tier === 'batch' ? 'var(--text-strong)' : 'var(--status-success-text)',
                      }}>
                        Latency: {call.latency_tier}{call.latency_budget_ms ? ` (${call.latency_budget_ms}ms)` : ''}
                      </span>
                    )}
                    {call.output_criticality && (
                      <span style={{
                        padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600',
                        background: call.output_criticality === 'safety_critical' ? 'var(--status-error-bg)' : call.output_criticality === 'high' ? 'var(--status-warning-bg)' : 'var(--surface-muted)',
                        color: call.output_criticality === 'safety_critical' ? 'var(--status-error-text)' : call.output_criticality === 'high' ? 'var(--status-warning-text)' : 'var(--text-strong)',
                      }}>
                        Criticality: {call.output_criticality.replace(/_/g, ' ')}
                      </span>
                    )}
                    {call.volume_pattern && (
                      <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', background: 'var(--status-purple-bg)', color: 'var(--status-purple-text)' }}>
                        {call.volume_pattern.replace(/_/g, ' ')}{call.estimated_weekly_volume_per_user ? ` (~${call.estimated_weekly_volume_per_user}/user/wk)` : ''}
                      </span>
                    )}
                  </div>

                  {/* Token & Reasoning Details */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                    <div>
                      <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '2px' }}>Input Tokens (base)</div>
                      {/* "—" not 0: a missing estimate is unknown, and 0 here is what used to price calls as free. */}
                      <div style={{ fontSize: '14px', fontWeight: '600', color: call.estimated_input_tokens != null ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
                        {call.estimated_input_tokens != null ? call.estimated_input_tokens.toLocaleString() : '—'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '2px' }}>Input w/ Context</div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: call.estimated_input_tokens_with_context ? 'var(--cedar-primary)' : 'var(--text-tertiary)' }}>
                        {call.estimated_input_tokens_with_context ? call.estimated_input_tokens_with_context.toLocaleString() : '—'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '2px' }}>Output Tokens</div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: call.estimated_output_tokens != null ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
                        {call.estimated_output_tokens != null ? call.estimated_output_tokens.toLocaleString() : '—'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '2px' }}>Reasoning Tokens</div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: call.estimated_reasoning_tokens ? 'var(--status-warning-text)' : 'var(--text-tertiary)' }}>
                        {call.estimated_reasoning_tokens ? `~${call.estimated_reasoning_tokens.toLocaleString()}` : '—'}
                      </div>
                    </div>
                  </div>

                  {/* Reasoning Justification */}
                  {call.reasoning_justification && (
                    <div style={{ marginBottom: '14px' }}>
                      <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--status-warning-text)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Reasoning Justification</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.5', fontStyle: 'italic' }}>{call.reasoning_justification}</div>
                    </div>
                  )}

                  {/* Accuracy Requirement */}
                  {call.accuracy_requirement && (
                    <div style={{ marginBottom: '14px' }}>
                      <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Accuracy Requirement</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>{call.accuracy_requirement}</div>
                    </div>
                  )}

                  {/* Legacy description fallback */}
                  {!call.system_role && call.description_short && (
                    <div style={{ marginBottom: '14px' }}>
                      <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Description</div>
                      <div style={{ fontSize: '13px', color: 'var(--text-strong)', lineHeight: '1.6' }}>{call.description_short}</div>
                    </div>
                  )}

                  {/* Frequency */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                    {/* `??` not `||`: frequency 0 is a real measurement (e.g.
                        Essentials has no ad-hoc chat) — only null is unknown. */}
                    <div>
                      <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '2px' }}>Freq (Trial)</div>
                      <div style={{ fontSize: '13px', fontWeight: '600' }}>{call.frequency_trial ?? '—'}/wk</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '2px' }}>Freq (Full)</div>
                      <div style={{ fontSize: '13px', fontWeight: '600' }}>{call.frequency_full ?? '—'}/wk</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '2px' }}>Freq (Extended)</div>
                      <div style={{ fontSize: '13px', fontWeight: '600' }}>{call.frequency_extended ?? '—'}/wk</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Empty State */}
      {calls.length === 0 && (
        <div style={{ 
          textAlign: 'center', 
          padding: '60px 20px',
          color: 'var(--text-tertiary)'
        }}>
          <Info size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
          <p style={{ fontSize: '16px', marginBottom: '8px' }}>No calls found for this category</p>
          <p style={{ fontSize: '14px' }}>Try selecting "All Categories" or run the seeding script</p>
        </div>
      )}
      
      {/* Help Text */}
      <div style={{
        marginTop: '32px',
        padding: '16px',
        background: 'var(--surface-subtle)',
        borderRadius: '8px',
        fontSize: '13px',
        color: 'var(--text-secondary)',
        lineHeight: '1.6'
      }}>
        <strong style={{ color: 'var(--cedar-primary)' }}>How to use:</strong>
        <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
          <li>Click "Edit" to change model or parameters for any call</li>
          <li>Click "Info" icon to see call description and token estimates</li>
          <li>Costs here are <strong>modeled</strong>: registry token estimates × current model_definitions prices. "—" means no estimate — never assume $0. Measured spend (llm_usage_actual) lives in LLM Usage Analytics</li>
          <li>Filter by tier to see cost impact for Trial, Full, or Extended users</li>
          <li>Changes take effect immediately (next LLM call uses new config)</li>
        </ul>
      </div>
    </div>
  );
}
